import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, ChevronDown } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import LSCMonogram from "@/components/LSCMonogram";
import ServicesNav, { SERVICES_COLUMNS } from "@/components/ServicesNav";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "Our Approach" },
  { to: "/locations", label: "Locations" },
  { to: "/journal", label: "Journal" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  // Close the mobile menu (and its accordion) and return to the top whenever the route changes.
  useEffect(() => {
    setOpen(false);
    setMobileServicesOpen(false);
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  // Solid ivory bar on every page so the menu always reads clearly.
  const useLightSurface = true;
  const surfaceCls = useLightSurface
    ? "text-ink bg-ivory/95 backdrop-blur-md hairline-bottom shadow-[0_2px_20px_-12px_rgba(30,40,55,0.25)]"
    : "text-ivory bg-transparent";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${surfaceCls}`}
      data-testid="site-header"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="group flex items-center gap-3 md:gap-4 shrink-0" data-testid="brand-logo-link">
          <LSCMonogram size={44} tone={useLightSurface ? "navy" : "ivory"} />
          <BrandMark variant={useLightSurface ? "dark" : "light"} size="md" />
        </Link>

        <nav className="hidden nav-lg:flex items-center gap-3 2xl:gap-6" data-testid="primary-nav">
          {navItems.slice(0, 1).map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid={`nav-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {it.label}
            </NavLink>
          ))}
          <ServicesNav onDark={!useLightSurface} />
          {navItems.slice(1).map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid={`nav-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {it.label}
            </NavLink>
          ))}
          {user && user !== false ? (
            <>
              <Link
                to={user.role === "admin" ? "/admin" : "/portal"}
                className="nav-link"
                data-testid="nav-link-portal"
              >
                {user.role === "admin" ? "Admin" : "Portal"}
              </Link>
              <button
                onClick={logout}
                className="nav-link"
                data-testid="nav-logout-button"
              >
                Sign Out
              </button>
            </>
          ) : (
            <Link to="/login" className="nav-link" data-testid="nav-link-login">
              Client Portal
            </Link>
          )}
        </nav>

        <div className="hidden nav-lg:flex items-center shrink-0 ml-3">
          <Link to="/intake" className="btn-gold !py-2.5 !px-5 2xl:!px-6" data-testid="nav-cta-intake">
            Request Counsel
          </Link>
        </div>

        <button
          className="nav-lg:hidden p-2"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="nav-lg:hidden bg-ivory text-ink hairline-bottom max-h-[calc(100vh-92px)] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-8 flex flex-col gap-5">
            {navItems.slice(0, 1).map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={() => setOpen(false)}
                className="nav-link"
                data-testid={`mobile-nav-${it.label.toLowerCase()}`}
              >
                {it.label}
              </NavLink>
            ))}

            {/* Mobile Services — collapsible accordion mirroring the desktop dropdown */}
            <div className="pt-3 pb-2 border-t border-brass/25" data-testid="mobile-services-group">
              <button
                type="button"
                onClick={() => setMobileServicesOpen((v) => !v)}
                className="nav-link flex items-center gap-2 w-full text-left"
                aria-expanded={mobileServicesOpen}
                aria-controls="mobile-services-panel"
                data-testid="mobile-nav-services-toggle"
              >
                <span>Services</span>
                <ChevronDown
                  size={13}
                  className={`transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`}
                />
              </button>

              <div
                id="mobile-services-panel"
                className={`grid transition-[grid-template-rows] duration-300 ease-out ${mobileServicesOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                data-testid="mobile-services-panel"
              >
                <div className="overflow-hidden">
                  <div className="mt-4 space-y-6">
                    {SERVICES_COLUMNS.map((col, ci) => (
                      <div key={col.heading} className="pb-1" data-testid={`mobile-services-col-${ci}`}>
                        <div className="text-[9px] uppercase tracking-[0.32em] text-brass font-medium">
                          {col.heading}
                        </div>
                        <div className="mt-1 font-cormorant italic text-[13px] text-slate2/80 leading-snug">
                          {col.accent}
                        </div>
                        <ul className="mt-3 space-y-3">
                          {col.items.map((item) => {
                            const isAnchor = item.href.includes("#");
                            const testId = `mobile-services-item-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`;
                            const inner = (
                              <>
                                <div className={`font-display text-navy leading-tight ${item.featured ? "text-[15px]" : "text-[13.5px]"}`}>
                                  {item.label}
                                </div>
                                <div className="mt-0.5 text-[11px] text-slate2 font-light leading-snug">
                                  {item.desc}
                                </div>
                              </>
                            );
                            return (
                              <li key={item.label}>
                                {isAnchor ? (
                                  <a
                                    href={item.href}
                                    onClick={() => setOpen(false)}
                                    className={`block ${item.featured ? "hairline-top pt-3" : ""}`}
                                    data-testid={testId}
                                  >
                                    {inner}
                                  </a>
                                ) : (
                                  <Link
                                    to={item.href}
                                    onClick={() => setOpen(false)}
                                    className={`block ${item.featured ? "hairline-top pt-3" : ""}`}
                                    data-testid={testId}
                                  >
                                    {inner}
                                  </Link>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                    <div className="pt-3 hairline-top">
                      <Link
                        to="/practice"
                        onClick={() => setOpen(false)}
                        className="text-[10px] uppercase tracking-[0.28em] text-oxblood font-medium"
                        data-testid="mobile-services-all"
                      >
                        Browse the Full Continuum →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {navItems.slice(1).map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={() => setOpen(false)}
                className="nav-link"
                data-testid={`mobile-nav-${it.label.toLowerCase()}`}
              >
                {it.label}
              </NavLink>
            ))}
            {user && user !== false ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : "/portal"} className="nav-link" onClick={() => setOpen(false)}>
                  {user.role === "admin" ? "Admin" : "Portal"}
                </Link>
                <button className="nav-link text-left" onClick={() => { logout(); setOpen(false); }}>
                  Sign Out
                </button>
              </>
            ) : (
              <Link to="/login" className="nav-link" onClick={() => setOpen(false)}>Client Portal</Link>
            )}
            <Link to="/intake" className="btn-gold mt-4 self-start" onClick={() => setOpen(false)}>
              Request Counsel
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
